import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';
import {
  ButtonColor,
  ButtonVariant,
  DailyDialer,
  Icon,
  ManageFolderModal,
  TextButton,
  WebSocketDialer,
} from '../../../components';
import { useAppSelector, useFeatureFlag, useGetUserPermissions } from '../../../hooks';
import { useGetFolderByIdQuery } from '../../../services';
import { BaseFolder, Folder, LD_FeatureFlags, Permissions, PracticeProspect } from '../../../types';
import { getNextSimInQueueDetails } from '../../../utils';
import SimActionModals, { SimActionType } from '../SimActionModals';
import ExplorerColumn from './ExplorerColumn';
import { ExplorerFolderItem } from './ExplorerFolderItem';
import { ExplorerSimulationItem } from './ExplorerSimulationItem';
import FolderActionModals, { FolderActionType } from './FolderActionModals';
import { SimulationDetails, useSimulationDetailsHeaderActions } from './SimulationDetails';

const SIM_DETAILS_WIDTH = 288;

type FolderTreeColumnProps = {
  folders: Folder[];
  isLoading: boolean;
  openSimulationId: string | null;
  prospects: PracticeProspect[];
  setOpenSimulationId: Dispatch<SetStateAction<string | null>>;
  parentFolder?: BaseFolder;
  setHasOpenFolder?: Dispatch<SetStateAction<boolean>>;
};

/**
 * Recursive component that handles the folder hierarchy and navigation.
 * Each instance represents a level in the folder tree.
 */
const FolderTreeColumn = ({
  folders,
  isLoading,
  openSimulationId,
  prospects,
  setOpenSimulationId,
  parentFolder,
  setHasOpenFolder,
}: FolderTreeColumnProps) => {
  const [displayedProspects, setDisplayedProspects] = useState<PracticeProspect[]>([]);
  // Tracks which folder modal is open and which folder it's operating on.
  const [folderActionModal, setFolderActionModal] = useState<{ type: FolderActionType; folder: Folder }>();
  const [isAddFolderModalOpen, setIsAddFolderModalOpen] = useState(false);
  // Controls whether or not to open the dialer with the current open simulation.
  const [isInCallMode, setIsInCallMode] = useState(false);
  const [openFolderId, setOpenFolderId] = useState<string | null>(null);
  // The sim action modal that is currently open, along with the prospect that is being acted on.
  const [openSimModal, setOpenSimModal] = useState<{ type: SimActionType; prospect: PracticeProspect } | null>(null);
  const [hasOpenChildFolder, setHasOpenChildFolder] = useState(false);

  const dailyFF = useFeatureFlag(LD_FeatureFlags.RELEASE_DAILY_FOR_WEB_CALLS);

  const { user } = useAppSelector((state) => state.auth);
  const permissions = user?.permissions || [];
  const canManageSims = !!permissions.includes(Permissions.MANAGE_PROSPECT);

  const { canUserEditLibrary } = useGetUserPermissions();

  const { data: openFolderData, isLoading: isLoadingOpenFolderData } = useGetFolderByIdQuery(
    { id: openFolderId || '' },
    { skip: !openFolderId }
  );

  const openSimulation = useMemo(
    () => displayedProspects.find((prospect) => prospect.personaId === openSimulationId),
    [displayedProspects, openSimulationId]
  );

  const nextSimInQueue = getNextSimInQueueDetails(displayedProspects, openSimulation);

  const toggleOpenFolder = useCallback(
    (folder: Folder) => {
      // If the folder is already open, close it,
      // Otherwise, open it.
      setOpenFolderId((prev) => (prev === folder.id ? null : folder.id));
      // Close any open simulations in this column.
      setOpenSimulationId(null);
    },
    [setOpenSimulationId]
  );

  const toggleOpenSimulation = useCallback(
    (prospect: PracticeProspect) => {
      // Toggle simulation open/closed state.
      setOpenSimulationId((prev) => (prev === prospect.personaId ? null : prospect.personaId));
      // Close any open folders in this column.
      setOpenFolderId(null);
    },
    [setOpenSimulationId]
  );

  // Updates the UI state to reflect the deletion/moving of a folder.
  // If the folder that was deleted/moved is the currently open folder, close it.
  const onDeleteOrMoveFolderSuccess = useCallback(
    (id: string) => setOpenFolderId((prev) => (prev === id ? null : prev)),
    []
  );

  // Effect to close any open subfolder when the parent folder changes.
  // This ensures that when a parent folder is closed or changed,
  // all child FolderTreeColumns reset their open folder state.
  useEffect(() => {
    setOpenFolderId(null);
  }, [parentFolder]);

  useEffect(() => {
    if (setHasOpenFolder) {
      setHasOpenFolder(!!openFolderId || !!openSimulationId);
    }
  }, [openFolderId, openSimulationId, setHasOpenFolder]);

  useEffect(() => {
    setDisplayedProspects(prospects);
  }, [prospects]);

  const sharedDialerProps = useMemo(
    () =>
      openSimulation
        ? {
            currProspect: openSimulation,
            callNextProspect: () => setOpenSimulationId(nextSimInQueue?.prospect.personaId || null),
            closeDialer: () => setIsInCallMode(false),
            setProspects: setDisplayedProspects,
            nextProspectDetails: nextSimInQueue,
          }
        : undefined,
    [openSimulation, nextSimInQueue, setDisplayedProspects, setOpenSimulationId]
  );

  return (
    <div className="flex">
      <ExplorerColumn isLoading={isLoading}>
        {folders.map((folder) => (
          <ExplorerFolderItem
            key={folder.id}
            isOpen={openFolderId === folder.id}
            isUsed={folder.isUsed}
            name={folder.name}
            openFolderActionModal={(type) => setFolderActionModal({ type, folder })}
            toggleOpen={() => toggleOpenFolder(folder)}
            isLevelBeforeLast={!hasOpenChildFolder}
          />
        ))}
        {displayedProspects.map((prospect) => (
          <ExplorerSimulationItem
            key={prospect.personaId}
            active={openSimulationId === prospect.personaId}
            canManageSims={canManageSims}
            prospect={prospect}
            openSimModal={(type) => setOpenSimModal({ type, prospect })}
            toggleOpenSimulationDetails={() => toggleOpenSimulation(prospect)}
          />
        ))}
        {canUserEditLibrary && !isLoading && (
          <div className="flex h-14 justify-center">
            <TextButton
              startIcon={Icon.PLUS}
              color={ButtonColor.NEUTRAL}
              text="Add folder"
              variant={ButtonVariant.GHOST}
              onClick={() => setIsAddFolderModalOpen(true)}
            />
          </div>
        )}
      </ExplorerColumn>
      {/* Only render child column when folder is open, ignoring any stale data from previously opened folders. */}
      {openFolderId && (
        <FolderTreeColumn
          folders={openFolderData?.subFolders || []}
          isLoading={isLoadingOpenFolderData}
          openSimulationId={openSimulationId}
          parentFolder={openFolderData?.folder}
          prospects={openFolderData?.prospects?.data || []}
          setOpenSimulationId={setOpenSimulationId}
          setHasOpenFolder={setHasOpenChildFolder}
        />
      )}
      {openSimulation && (
        <ExplorerColumn
          width={SIM_DETAILS_WIDTH}
          useActions={() =>
            useSimulationDetailsHeaderActions({
              isDraft: openSimulation.isDraft,
              isUsed: openSimulation.isUsed,
              personaId: openSimulation.personaId,
              prospectCategory: openSimulation.category,
              prospectUserId: openSimulation.userId,
              status: openSimulation.status,
              openSimModal: (type) => setOpenSimModal({ type, prospect: openSimulation }),
            })
          }
        >
          <SimulationDetails prospect={openSimulation} runCall={() => setIsInCallMode(true)} />
        </ExplorerColumn>
      )}
      {folderActionModal && (
        <FolderActionModals
          folderModal={folderActionModal}
          closeModal={() => setFolderActionModal(undefined)}
          onDeleteOrMoveSuccess={() => onDeleteOrMoveFolderSuccess(folderActionModal.folder.id)}
        />
      )}
      {openSimModal && <SimActionModals simModal={openSimModal} closeModal={() => setOpenSimModal(null)} />}
      {isAddFolderModalOpen && (
        <ManageFolderModal folderParentId={parentFolder?.id} closeModal={() => setIsAddFolderModalOpen(false)} />
      )}
      {isInCallMode && sharedDialerProps && !dailyFF && <WebSocketDialer {...sharedDialerProps} />}
      {isInCallMode && sharedDialerProps && dailyFF && <DailyDialer {...sharedDialerProps} />}
    </div>
  );
};

export default FolderTreeColumn;
